import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import './LegalPage.css';

export default function PrivacyPage() {
  const navigate = useNavigate();
  const { t, currentLanguage } = useLanguage();
  const lang = currentLanguage === 'nl' || currentLanguage === 'ar' ? currentLanguage : 'en';

  type Section = {
    title: string;
    paragraphs?: string[];
    bullets?: string[];
  };

  const CONTENT: Record<'en' | 'nl' | 'ar', {
    title: string;
    effective: string;
    jurisdiction: string;
    intro: string;
    sections: Section[];
    disclaimer: string;
  }> = {
    en: {
      title: 'Privacy Policy',
      effective: 'Effective Apr 2026',
      jurisdiction: 'GDPR / Netherlands',
      intro: 'This Privacy Policy explains how TheoryCoach processes personal data when you use the app, website, onboarding flows, and subscription services.',
      sections: [
        {
          title: '1. Data controller and scope',
          paragraphs: [
            'TheoryCoach acts as the controller for account, learning, and subscription-related personal data processed through the TheoryCoach app.',
            'This policy applies to users in the Netherlands and the European Economic Area and is designed to align with the General Data Protection Regulation (GDPR).',
          ],
        },
        {
          title: '2. Categories of personal data',
          bullets: [
            'Account data: email address, authentication identifiers, and profile metadata.',
            'Learning data: completed tests, scores, progress signals, and exam-date preferences.',
            'Subscription data: selected plan tier, payment state, billing cycle metadata, and cancellation status.',
            'Technical data: device/session identifiers, app diagnostics, and anti-abuse security logs.',
          ],
        },
        {
          title: '3. Legal bases under GDPR',
          bullets: [
            'Performance of contract (Art. 6(1)(b)): delivering practice tests, AI exam workflows, and account features.',
            'Legitimate interests (Art. 6(1)(f)): security, fraud prevention, product analytics, and service reliability.',
            'Legal obligations (Art. 6(1)(c)): financial and accounting compliance where required.',
            'Consent (Art. 6(1)(a)): only where legally required for optional processing.',
          ],
        },
        {
          title: '4. Processing purposes',
          bullets: [
            'Authenticate users and secure account access.',
            'Personalize study recommendations and progress dashboards.',
            'Provide subscription checkout, payment verification, and entitlement management.',
            'Monitor service performance, prevent misuse, and debug incidents.',
          ],
        },
        {
          title: '5. Processors and third-party services',
          paragraphs: [
            'TheoryCoach uses infrastructure providers acting as processors under data processing terms.',
          ],
          bullets: [
            'Supabase: authentication, database, and profile data storage.',
            'Stripe: payment processing, subscription billing, and checkout security.',
          ],
        },
        {
          title: '6. International transfers',
          paragraphs: [
            'Where processing includes cross-border transfers, TheoryCoach relies on GDPR-compliant transfer mechanisms, including Standard Contractual Clauses where applicable.',
          ],
        },
        {
          title: '7. Data retention',
          bullets: [
            'Active account data is retained while your account is active.',
            'Billing and transaction records are retained for required financial compliance periods.',
            'Security and diagnostic logs are retained only for limited operational periods unless needed for abuse investigations or legal obligations.',
          ],
        },
        {
          title: '8. Your rights',
          bullets: [
            'Right of access, rectification, erasure, restriction, portability, and objection.',
            'Right to withdraw consent where processing is consent-based.',
            'Right to lodge a complaint with the Dutch Data Protection Authority (Autoriteit Persoonsgegevens).',
          ],
          paragraphs: [
            'You can submit requests through TheoryCoach in-app support in Settings. Identity verification may be required before fulfillment.',
          ],
        },
        {
          title: '9. Security measures',
          paragraphs: [
            'TheoryCoach applies technical and organizational controls, including authenticated access controls, transport security, and processor-level infrastructure safeguards.',
          ],
        },
        {
          title: '10. Policy updates',
          paragraphs: [
            'TheoryCoach may update this policy as product features or legal requirements evolve. Material updates are published in-app with a revised effective date.',
          ],
        },
      ],
      disclaimer: 'This policy text is product-level information and does not constitute legal advice.',
    },
    nl: {
      title: 'Privacybeleid',
      effective: 'Ingangsdatum apr 2026',
      jurisdiction: 'AVG / Nederland',
      intro: 'Dit Privacybeleid beschrijft hoe TheoryCoach persoonsgegevens verwerkt wanneer je de app, website, onboarding en abonnementsdiensten gebruikt.',
      sections: [
        {
          title: '1. Verwerkingsverantwoordelijke en reikwijdte',
          paragraphs: [
            'TheoryCoach treedt op als verwerkingsverantwoordelijke voor account-, leer- en abonnementsgegevens binnen de TheoryCoach-app.',
            'Dit beleid geldt voor gebruikers in Nederland en de Europese Economische Ruimte en is afgestemd op de AVG (GDPR).',
          ],
        },
        {
          title: '2. Categorieën persoonsgegevens',
          bullets: [
            'Accountgegevens: e-mailadres, authenticatie-ID’s en profielmetadata.',
            'Leerdata: afgeronde toetsen, scores, voortgangssignalen en examendatum-voorkeuren.',
            'Abonnementsdata: gekozen plan, betaalstatus, facturatiecyclusmetadata en opzegstatus.',
            'Technische data: apparaat-/sessie-ID’s, app-diagnostiek en beveiligingslogs.',
          ],
        },
        {
          title: '3. Rechtsgronden onder de AVG',
          bullets: [
            'Uitvoering van overeenkomst (art. 6 lid 1 sub b AVG): levering van oefentoetsen, AI-examenflows en accountfunctionaliteit.',
            'Gerechtvaardigd belang (art. 6 lid 1 sub f AVG): beveiliging, fraudepreventie, productanalyse en betrouwbaarheid.',
            'Wettelijke verplichting (art. 6 lid 1 sub c AVG): fiscale/administratieve naleving waar vereist.',
            'Toestemming (art. 6 lid 1 sub a AVG): uitsluitend waar juridisch verplicht voor optionele verwerking.',
          ],
        },
        {
          title: '4. Verwerkingsdoeleinden',
          bullets: [
            'Authenticatie en beveiliging van accounttoegang.',
            'Personaliseren van leeradviezen en voortgangsdashboards.',
            'Uitvoeren van abonnement-checkout, betaalverificatie en entitlementbeheer.',
            'Monitoren van prestaties, voorkomen van misbruik en oplossen van incidenten.',
          ],
        },
        {
          title: '5. Verwerkers en derde diensten',
          paragraphs: [
            'TheoryCoach gebruikt infrastructuurleveranciers als verwerkers onder verwerkersafspraken.',
          ],
          bullets: [
            'Supabase: authenticatie, database en opslag van profieldata.',
            'Stripe: betalingsverwerking, abonnement-facturatie en checkoutbeveiliging.',
          ],
        },
        {
          title: '6. Internationale doorgiften',
          paragraphs: [
            'Waar verwerking grensoverschrijdende doorgifte omvat, gebruikt TheoryCoach AVG-conforme doorgiftemechanismen, waaronder standaardcontractbepalingen waar van toepassing.',
          ],
        },
        {
          title: '7. Bewaartermijnen',
          bullets: [
            'Actieve accountgegevens worden bewaard zolang je account actief is.',
            'Factuur- en transactiegegevens worden bewaard conform wettelijke bewaarplichten.',
            'Beveiligings- en diagnostische logs worden beperkt bewaard, tenzij langer nodig voor misbruikonderzoek of wettelijke verplichtingen.',
          ],
        },
        {
          title: '8. Jouw rechten',
          bullets: [
            'Recht op inzage, rectificatie, verwijdering, beperking, overdraagbaarheid en bezwaar.',
            'Recht om toestemming in te trekken wanneer verwerking daarop berust.',
            'Recht om een klacht in te dienen bij de Autoriteit Persoonsgegevens.',
          ],
          paragraphs: [
            'Verzoeken kun je indienen via de in-app support in Instellingen. Voor afhandeling kan identiteitsverificatie nodig zijn.',
          ],
        },
        {
          title: '9. Beveiligingsmaatregelen',
          paragraphs: [
            'TheoryCoach hanteert technische en organisatorische maatregelen, waaronder geauthenticeerde toegangscontrole, transportbeveiliging en infrastructuurmaatregelen bij verwerkers.',
          ],
        },
        {
          title: '10. Wijzigingen in dit beleid',
          paragraphs: [
            'TheoryCoach kan dit beleid aanpassen wanneer productfunctionaliteit of wetgeving verandert. Belangrijke wijzigingen worden in de app gepubliceerd met een nieuwe ingangsdatum.',
          ],
        },
      ],
      disclaimer: 'Deze beleidstekst is productinformatie en vormt geen juridisch advies.',
    },
    ar: {
      title: 'سياسة الخصوصية',
      effective: 'سارية من أبريل 2026',
      jurisdiction: 'GDPR / هولندا',
      intro: 'توضح هذه السياسة كيف يعالج TheoryCoach البيانات الشخصية عند استخدام التطبيق والموقع ومسارات الاشتراك.',
      sections: [
        {
          title: '1. جهة التحكم ونطاق التطبيق',
          paragraphs: [
            'يعمل TheoryCoach كجهة تحكم في بيانات الحساب والتعلم والاشتراك داخل التطبيق.',
            'تنطبق هذه السياسة على المستخدمين في هولندا والمنطقة الاقتصادية الأوروبية وتتوافق مع اللائحة العامة لحماية البيانات (GDPR).',
          ],
        },
        {
          title: '2. فئات البيانات الشخصية',
          bullets: [
            'بيانات الحساب: البريد الإلكتروني ومعرّفات المصادقة وبيانات الملف الشخصي.',
            'بيانات التعلم: الاختبارات المكتملة والنتائج وإشارات التقدم وتفضيلات تاريخ الامتحان.',
            'بيانات الاشتراك: الخطة المختارة وحالة الدفع وبيانات دورة الفوترة وحالة الإلغاء.',
            'البيانات التقنية: معرّفات الجهاز/الجلسة وسجلات التشخيص والأمان.',
          ],
        },
        {
          title: '3. الأساس القانوني وفق GDPR',
          bullets: [
            'تنفيذ العقد (المادة 6(1)(b)): تقديم الاختبارات ووظائف الحساب وتدفقات امتحان الذكاء الاصطناعي.',
            'المصلحة المشروعة (المادة 6(1)(f)): الأمان ومنع الاحتيال وتحليل المنتج وموثوقية الخدمة.',
            'الالتزام القانوني (المادة 6(1)(c)): الامتثال المالي والمحاسبي عند اللزوم.',
            'الموافقة (المادة 6(1)(a)): عند الحاجة القانونية للمعالجة الاختيارية.',
          ],
        },
        {
          title: '4. أغراض المعالجة',
          bullets: [
            'توثيق المستخدمين وتأمين الوصول للحساب.',
            'تخصيص توصيات الدراسة ولوحات التقدم.',
            'تشغيل الدفع والتحقق من السداد وإدارة صلاحيات الاشتراك.',
            'مراقبة الأداء ومنع الإساءة ومعالجة الأعطال.',
          ],
        },
        {
          title: '5. المعالجون والخدمات الخارجية',
          paragraphs: [
            'يستخدم TheoryCoach مزودي بنية تحتية يعملون كمعالجي بيانات بموجب اتفاقيات معالجة.',
          ],
          bullets: [
            'Supabase: المصادقة وقاعدة البيانات وتخزين بيانات الملف الشخصي.',
            'Stripe: معالجة الدفع وفوترة الاشتراك وأمان صفحة الدفع.',
          ],
        },
        {
          title: '6. نقل البيانات دولياً',
          paragraphs: [
            'عند وجود نقل عابر للحدود، يعتمد TheoryCoach آليات امتثال GDPR مثل البنود التعاقدية القياسية عند الاقتضاء.',
          ],
        },
        {
          title: '7. الاحتفاظ بالبيانات',
          bullets: [
            'تُحتفظ بيانات الحساب النشط طوال مدة بقاء الحساب فعالاً.',
            'تُحتفظ بيانات الفوترة والمعاملات وفق متطلبات الامتثال المالي.',
            'تُحتفظ سجلات الأمان والتشخيص لفترات تشغيلية محدودة ما لم تتطلب التحقيقات أو الالتزامات القانونية مدة أطول.',
          ],
        },
        {
          title: '8. حقوقك',
          bullets: [
            'الحق في الوصول والتصحيح والحذف والتقييد وقابلية النقل والاعتراض.',
            'الحق في سحب الموافقة عندما تكون المعالجة قائمة عليها.',
            'الحق في تقديم شكوى إلى هيئة حماية البيانات الهولندية (Autoriteit Persoonsgegevens).',
          ],
          paragraphs: [
            'يمكنك تقديم الطلبات عبر الدعم داخل التطبيق من الإعدادات، وقد نطلب التحقق من الهوية قبل التنفيذ.',
          ],
        },
        {
          title: '9. إجراءات الأمان',
          paragraphs: [
            'يطبق TheoryCoach ضوابط تقنية وتنظيمية تشمل ضوابط وصول موثقة وأمن النقل وضمانات بنية تحتية لدى المعالجين.',
          ],
        },
        {
          title: '10. تحديثات السياسة',
          paragraphs: [
            'قد يقوم TheoryCoach بتحديث هذه السياسة مع تطور الميزات أو المتطلبات القانونية. تُنشر التحديثات الجوهرية داخل التطبيق مع تاريخ سريان جديد.',
          ],
        },
      ],
      disclaimer: 'هذا النص معلومات سياسة على مستوى المنتج ولا يُعد استشارة قانونية.',
    },
  };
  const tx = CONTENT[lang];

  return (
    <div className="legal-page" dir={lang === 'ar' ? 'rtl' : 'ltr'}>
      <div className="legal-page-shell">
        <div className="legal-page-header">
          <div className="legal-page-header-left">
            <button
              type="button"
              className="legal-page-back"
              onClick={() => {
                if (window.history.length > 1) {
                  navigate(-1);
                  return;
                }
                navigate('/dashboard');
              }}
              aria-label={t('aria.goBack')}
            >
              {lang === 'ar' ? 'رجوع →' : lang === 'nl' ? '← Terug' : '← Back'}
            </button>
            <h1 className="legal-page-title">{tx.title}</h1>
          </div>
          <div className="legal-page-meta">
            <span className="legal-chip">{tx.effective}</span>
            <span className="legal-chip">{tx.jurisdiction}</span>
          </div>
        </div>

        <div className="legal-page-content">
          <p className="legal-intro">{tx.intro}</p>

          {tx.sections.map((section) => (
            <section className="legal-section" key={section.title}>
              <h2>{section.title}</h2>
              {section.paragraphs?.map((p, idx) => (
                <p key={`${section.title}-p-${idx}`}>{p}</p>
              ))}
              {section.bullets && (
                <ul>
                  {section.bullets.map((item, idx) => (
                    <li key={`${section.title}-li-${idx}`}>{item}</li>
                  ))}
                </ul>
              )}
            </section>
          ))}

          <p className="legal-disclaimer">{tx.disclaimer}</p>
          <p className="legal-updated">{tx.effective}</p>
        </div>
      </div>
    </div>
  );
}
